import { useMaximize } from "./hooks/useMaximize";
import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";
import { getAllPendingSales, updateSaleStatus } from "./db";

// scopeToUserId: when set, shows only that user's own failed sales (e.g.
// a cashier viewing their own list — the one identity that can actually
// pass process_sale's auth.uid() = p_cashier_id check on retry). When
// omitted, shows every failed sale across all cashiers, for manager
// oversight — that's the existing all-rows manager panel, unchanged.
export default function FailedSalesPanel({ onClose, scopeToUserId }) {
  const isSelfView = !!scopeToUserId;
  const [sales, setSales] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDismissId, setConfirmDismissId] = useState(null);
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);

  useEffect(() => {
    fetchCashiers();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeToUserId]);

  async function fetchCashiers() {
    const { data, error } = await supabase.from("profiles").select("id, full_name");
    logSupabaseError("FailedSalesPanel: fetchCashiers", error);
    if (data) setCashiers(data);
  }

  async function load() {
    setLoading(true);
    const all = await getAllPendingSales();
    setSales(
      all.filter(
        (s) =>
          s.status === "failed" &&
          (!isSelfView || s.p_cashier_id === scopeToUserId),
      ),
    );
    setLoading(false);
  }

  function cashierName(id) {
    return cashiers.find((c) => c.id === id)?.full_name ?? id ?? "—";
  }

  // process_sale rejects a replay whose caller isn't the cashier who
  // originally queued the sale (RAISE EXCEPTION 'Caller identity
  // mismatch'). Matched by substring since the exact wrapping
  // supabase-js/PostgREST applies around that raw Postgres message isn't
  // confirmed — the literal exception text should still appear in it.
  function isIdentityMismatch(error) {
    return (
      typeof error === "string" && error.includes("Caller identity mismatch")
    );
  }

  async function handleRetry(sale) {
    setBusyId(sale.local_id);
    try {
      const { error } = await supabase.rpc("process_sale", {
        p_cashier_id: sale.p_cashier_id,
        p_subtotal: sale.p_subtotal,
        p_tax_rate: sale.p_tax_rate,
        p_tax_amount: sale.p_tax_amount,
        p_total_amount: sale.p_total_amount,
        p_payment_method: sale.p_payment_method,
        p_amount_tendered: sale.p_amount_tendered,
        p_change_amount: sale.p_change_amount,
        p_customer_name: sale.p_customer_name,
        p_notes: sale.p_notes,
        p_items: sale.p_items,
      });
      if (error) throw error;
      await updateSaleStatus(sale.local_id, "synced");
    } catch (err) {
      await updateSaleStatus(sale.local_id, "failed", err.message);
    }
    setBusyId(null);
    await load();
  }

  async function handleDismiss(sale) {
    if (confirmDismissId !== sale.local_id) {
      setConfirmDismissId(sale.local_id);
      return;
    }
    setBusyId(sale.local_id);
    // Keep the original error on record — dismissing isn't the same as
    // clearing the audit trail of what went wrong.
    await updateSaleStatus(sale.local_id, "dismissed", sale.error);
    setBusyId(null);
    setConfirmDismissId(null);
    await load();
  }

  const totalAtRisk = sales.reduce(
    (sum, s) => sum + Number(s.p_total_amount || 0),
    0,
  );

  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "980px" }}>
        <div className="report-header">
          <div>
            <h2 className="report-title">
              {isSelfView ? "⚠️ My Failed Sales" : "⚠️ Failed Offline Sales"}
            </h2>
            <p className="report-date">
              {sales.length} sale{sales.length !== 1 ? "s" : ""}{" "}
              {isSelfView ? "of yours" : ""} failed to sync — review and
              retry or dismiss
            </p>
          </div>
          <button
            className="maximize-btn"
            onClick={toggle}
            title={icon === "⊞" ? "Maximize" : "Restore"}
          >
            {icon}
          </button>
          <button className="report-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <p className="report-loading">Loading...</p>
          ) : sales.length === 0 ? (
            <div className="report-empty">
              <p>✅</p>
              <p>No failed offline sales</p>
            </div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th>Queued</th>
                  <th>Cashier</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.local_id}>
                    <td className="time-col">
                      {sale.queued_at
                        ? new Date(sale.queued_at).toLocaleString()
                        : "—"}
                    </td>
                    <td>{cashierName(sale.p_cashier_id)}</td>
                    <td style={{ fontSize: "12px", maxWidth: "260px" }}>
                      {(sale.p_items || [])
                        .map((i) => `${i.quantity}× ${i.product_name}`)
                        .join(", ")}
                    </td>
                    <td className="amount-col">
                      ETB {Number(sale.p_total_amount ?? 0).toFixed(2)}
                    </td>
                    <td>{(sale.p_payment_method ?? "").toUpperCase()}</td>
                    <td style={{ color: "var(--red)", fontSize: "12px", maxWidth: "220px" }}>
                      {isIdentityMismatch(sale.error) ? (
                        isSelfView ? (
                          // In a self-scoped view every row already
                          // belongs to the signed-in viewer, so the
                          // mismatch is stale: it happened while someone
                          // else was signed in on this device. Retry now
                          // should actually succeed.
                          <span title={`Raw error: ${sale.error}`}>
                            <strong style={{ color: "var(--amber)" }}>
                              ⚠ Wrong user was signed in
                            </strong>
                            <div
                              style={{
                                color: "var(--text3)",
                                fontSize: "11px",
                                marginTop: "2px",
                              }}
                            >
                              This failed while someone else was signed in
                              — you're signed in now, so Retry should work.
                            </div>
                          </span>
                        ) : (
                          <span
                            title={`Requires manager access to this panel. If ${cashierName(sale.p_cashier_id)} is not a manager, this can't be retried from here and needs manual follow-up.\n\nRaw error: ${sale.error}`}
                          >
                            <strong style={{ color: "var(--amber)" }}>
                              ⚠ Different cashier's sale
                            </strong>
                            <div
                              style={{
                                color: "var(--text3)",
                                fontSize: "11px",
                                marginTop: "2px",
                              }}
                            >
                              Only {cashierName(sale.p_cashier_id)} can
                              complete this — have them sign in and use
                              Retry.
                            </div>
                          </span>
                        )
                      ) : (
                        sale.error || "Unknown error"
                      )}
                    </td>
                    <td>
                      <div className="action-btns">
                        <button
                          className="edit-btn"
                          disabled={busyId === sale.local_id}
                          onClick={() => handleRetry(sale)}
                          title="Retry this sale"
                        >
                          {busyId === sale.local_id ? "…" : "↻ Retry"}
                        </button>
                        <button
                          className="delete-btn"
                          disabled={busyId === sale.local_id}
                          onClick={() => handleDismiss(sale)}
                          title={
                            confirmDismissId === sale.local_id
                              ? "Click again to confirm — this cannot be undone"
                              : "Dismiss (ignore permanently)"
                          }
                        >
                          {confirmDismissId === sale.local_id
                            ? "Confirm?"
                            : "Dismiss"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="total-label">
                    TOTAL AT RISK ({sales.length})
                  </td>
                  <td className="total-amount">
                    ETB {totalAtRisk.toFixed(2)}
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
